'use client';

/**
 * BookingCalendar Component — Custom-built (no react-day-picker)
 *
 * Custom calendar built from scratch with CSS Grid + React state for range/single selection.
 *
 * Features:
 * - Real-time availability fetching from API
 * - Dynamic pricing display per date
 * - Range selection for lodges
 * - Single date selection for activities
 * - Mobile-responsive design
 * - Auto-refresh every 30s + on tab visibility
 *
 * @see lib/booking/availability.ts for availability logic
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  format,
  addMonths,
  subMonths,
  startOfMonth,
  endOfMonth,
  isBefore,
  isAfter,
  isSameDay,
  startOfToday,
  addDays,
} from 'date-fns';
import { es, enUS, pt } from 'date-fns/locale';
import styles from './BookingCalendar.module.css';
import { Currency } from '@/types/booking';
import { AvailabilityStatus } from '@/types/availability';
import { getSessionId } from '@/lib/session';

/* ============================================
   Types
   ============================================ */

interface DateAvailabilityData {
  date: string;
  status: AvailabilityStatus;
  price: number;
  currency: Currency;
  minNights?: number;
}

interface BookingCalendarProps {
  itemType: 'lodge' | 'activity';
  itemId: string;
  locale?: 'en' | 'es' | 'pt';
  minimumNights?: number;
  onRangeSelect?: (range: {
    start: Date;
    end: Date;
    cachedNightlyRates?: Record<string, number>;
    cacheComplete?: boolean;
  } | null) => void;
  onDateSelect?: (date: Date | null) => void;
  guests?: number;
  initialRange?: { start: Date; end: Date };
  initialDate?: Date;
  showPrices?: boolean;
  currency?: Currency;
  disabled?: boolean;
  className?: string;
  variant?: 'card' | 'flat';
  hideHeader?: boolean;
  hideLegend?: boolean;
  /**
   * Called whenever the effective minimum nights for the selected check-in date
   * changes. Fires with the listing-level default on mount; updates to the
   * per-date value once the user picks a check-in. Use this to keep parent
   * banners / hero stats in sync without re-fetching.
   */
  onEffectiveMinNightsChange?: (n: number) => void;
  /**
   * Admin-only authority flag (pay-link generation). When true, dates that are
   * merely on a TEMPORARY hold (status === 'hold') become selectable — the admin
   * pay-link flow has total control and may override a hold. PAID/confirmed
   * reservations ('booked') and external/manual blocks ('blocked') stay locked
   * even here. Held dates are rendered with a distinct green tint + tooltip so
   * the admin clearly SEES the hold being overridden.
   *
   * NEVER set this on the public customer calendar: customers must not be able
   * to grab dates someone else is already holding. Defaults to false, so the
   * customer flow is unaffected.
   */
  adminOverride?: boolean;
}

/* ============================================
   Constants
   ============================================ */

const DAY_HEADERS_EN = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const DAY_HEADERS_ES = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const DAY_HEADERS_PT = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

const CALENDAR_I18N = {
  en: {
    selectDates: 'Select your dates',
    selectDate: 'Select a date',
    minimumNights: (n: number) => `Minimum ${n} nights`,
    minNightsShort: (n: number) => `Min ${n} nights`,
    prevMonth: 'Previous month',
    nextMonth: 'Next month',
    available: 'Available',
    selected: 'Selected',
    unavailable: 'Unavailable',
    date: 'Date:',
    price: 'Price:',
    error: 'Unable to load availability. Please try again.',
  },
  es: {
    selectDates: 'Selecciona tus fechas',
    selectDate: 'Selecciona una fecha',
    minimumNights: (n: number) => `Mínimo ${n} noches`,
    minNightsShort: (n: number) => `Mín ${n} noches`,
    prevMonth: 'Mes anterior',
    nextMonth: 'Mes siguiente',
    available: 'Disponible',
    selected: 'Seleccionado',
    unavailable: 'No disponible',
    date: 'Fecha:',
    price: 'Precio:',
    error: 'No se pudo cargar la disponibilidad. Intenta de nuevo.',
  },
  pt: {
    selectDates: 'Selecione suas datas',
    selectDate: 'Selecione uma data',
    minimumNights: (n: number) => `Mínimo ${n} noites`,
    minNightsShort: (n: number) => `Mín ${n} noites`,
    prevMonth: 'Mês anterior',
    nextMonth: 'Próximo mês',
    available: 'Disponível',
    selected: 'Selecionado',
    unavailable: 'Indisponível',
    date: 'Data:',
    price: 'Preço:',
    error: 'Não foi possível carregar a disponibilidade. Tente novamente.',
  },
};

/* ============================================
   Helpers
   ============================================ */

/** Generate calendar cells: leading nulls + Date objects for each day */
function generateCalendarDays(year: number, month: number): (Date | null)[] {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startDayOfWeek = firstDay.getDay(); // 0 = Sunday
  const daysInMonth = lastDay.getDate();

  const cells: (Date | null)[] = [];
  for (let i = 0; i < startDayOfWeek; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(year, month, d));
  return cells;
}

/** Format price for display */
function formatPriceDisplay(amount: number, currency: Currency): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

/** Check if we already have availability data for a given month */
function hasMonthData(
  availability: Map<string, DateAvailabilityData>,
  year: number,
  month: number
): boolean {
  const today = startOfToday();
  const lastDay = new Date(year, month + 1, 0).getDate();
  let relevant = 0;
  let withData = 0;
  for (let d = 1; d <= lastDay; d++) {
    const date = new Date(year, month, d);
    if (isBefore(date, today)) continue;
    relevant++;
    if (availability.has(format(date, 'yyyy-MM-dd'))) withData++;
  }
  return relevant === 0 || withData >= relevant * 0.5;
}

/* ============================================
   Component
   ============================================ */

export default function BookingCalendar({
  itemType,
  itemId,
  locale = 'en',
  minimumNights = 1,
  onRangeSelect,
  onDateSelect,
  guests = 2,
  initialRange,
  initialDate,
  showPrices = false,
  currency = 'USD',
  disabled = false,
  className,
  variant = 'card',
  hideHeader = false,
  hideLegend = false,
  adminOverride = false,
  onEffectiveMinNightsChange,
}: BookingCalendarProps) {
  // ---- State ----
  const [rangeStart, setRangeStart] = useState<Date | null>(
    initialRange?.start || null
  );
  const [rangeEnd, setRangeEnd] = useState<Date | null>(
    initialRange?.end || null
  );
  const [selectedDate, setSelectedDate] = useState<Date | null>(
    initialDate || null
  );
  const [currentMonth, setCurrentMonth] = useState<Date>(
    initialRange?.start || initialDate || startOfToday()
  );
  const [availability, setAvailability] = useState<
    Map<string, DateAvailabilityData>
  >(new Map());
  // Meses (yyyy-MM) cuyo fetch de disponibilidad YA terminó. El esqueleto se decide
  // POR MES con esto, no con un flag global: así, al clickear rápido las flechas, un
  // fetch viejo que termina no puede "destapar" la grilla de un mes que aún no cargó.
  // Empieza vacío → el esqueleto se ve desde el primer render hasta el primer fetch.
  const [loadedMonths, setLoadedMonths] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [refreshed, setRefreshed] = useState(false);
  // Minimum nights fetched for the selected check-in date (backup fetch fallback)
  const [checkInMinNights, setCheckInMinNights] = useState<number | null>(null);

  // ---- Refs ----
  const abortControllerRef = useRef<AbortController | null>(null);
  const fetchedMonthsRef = useRef<Set<string>>(new Set());
  const refreshTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const availabilityRef = useRef<Map<string, DateAvailabilityData>>(new Map());

  // ---- Derived values ----
  const dateLocale = locale === 'es' ? es : locale === 'pt' ? pt : enUS;
  const i18n = CALENDAR_I18N[locale] || CALENDAR_I18N.en;
  const tomorrow = addDays(startOfToday(), 1);
  const dayHeaders = locale === 'es' ? DAY_HEADERS_ES : locale === 'pt' ? DAY_HEADERS_PT : DAY_HEADERS_EN;
  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  // El esqueleto se mantiene hasta que el mes VISIBLE haya terminado de cargar. Al
  // estar atado al mes mostrado (no a un flag global) es inmune al clickeo rápido
  // entre meses. En caso de error mostramos el mensaje + grilla, no esqueleto eterno.
  const currentMonthLoaded = loadedMonths.has(format(currentMonth, 'yyyy-MM'));
  const showSkeleton = !currentMonthLoaded && !error;
  const calendarDays = generateCalendarDays(year, month);
  const monthName = format(currentMonth, 'MMMM yyyy', { locale: dateLocale });
  const canGoPrev = isAfter(
    startOfMonth(currentMonth),
    startOfMonth(startOfToday())
  );

  // Per-check-in minimum nights: per-date Map > backup fetch > listing-level prop.
  // Per-date can be LESS than the listing-level minimum — NO Math.max, just cascade.
  const checkInDateStr = rangeStart ? format(rangeStart, 'yyyy-MM-dd') : null;
  const perDateMinNights = checkInDateStr
    ? availability.get(checkInDateStr)?.minNights
    : undefined;
  const effectiveMinNights = perDateMinNights ?? checkInMinNights ?? minimumNights;

  // Notify parent whenever the effective minimum changes (per-date or listing-level).
  useEffect(() => {
    onEffectiveMinNightsChange?.(effectiveMinNights);
  }, [effectiveMinNights, onEffectiveMinNightsChange]);

  // Anchor cell for the "Mín N noches" floating label rendered over the veto window.
  // Impar → floor(blockedCount/2); par → blockedCount/2 - 1 (both 0-based from rangeStart+1).
  const blockedCount = effectiveMinNights - 1;
  let anchorDate: Date | null = null;
  if (rangeStart && !rangeEnd && blockedCount > 0) {
    const anchorOffset =
      blockedCount % 2 === 1
        ? Math.floor(blockedCount / 2)
        : blockedCount / 2 - 1;
    anchorDate = addDays(rangeStart, 1 + anchorOffset);
  }

  // ---- Date checks ----

  // Single source of truth for "this status blocks selection". Under
  // `adminOverride` a *temporary* hold does NOT block (the admin pay-link flow
  // can override holds); paid 'booked' and external/manual 'blocked' always
  // block, even for admins. Customers never pass adminOverride, so for them all
  // three keep blocking exactly as before.
  const isBlockingStatus = useCallback(
    (status: AvailabilityStatus | undefined): boolean => {
      if (!status) return false;
      if (adminOverride && status === 'hold') return false;
      return status === 'booked' || status === 'blocked' || status === 'hold';
    },
    [adminOverride]
  );

  /** A hold the admin is allowed to override — drives the green visual cue. */
  const isOverridableHold = useCallback(
    (date: Date): boolean => {
      if (!adminOverride) return false;
      const dateData = availability.get(format(date, 'yyyy-MM-dd'));
      return dateData?.status === 'hold';
    },
    [adminOverride, availability]
  );

  // ---- Visual SHAPE helpers (admin override only) ----
  // A booking/hold occupies a NIGHT, not a full day, so its first/last day is a
  // diagonal half-cell (check-in: morning free ╱ afternoon occupied; check-out:
  // night occupied ╱ morning free). Under `adminOverride` a hold stays
  // *selectable* (see isBlockingStatus) yet must keep those night diagonals —
  // just tinted green. These helpers compute the SHAPE from the raw status
  // (hold counts as occupied here, unlike isBlockingStatus), independent of
  // selectability. Colour is then chosen per-cell: green for a hold, gray for a
  // real booked/blocked. The public calendar never takes this path.
  const statusOn = useCallback(
    (date: Date): AvailabilityStatus | undefined =>
      availability.get(format(date, 'yyyy-MM-dd'))?.status,
    [availability]
  );

  const isVisuallyOccupied = useCallback(
    (status: AvailabilityStatus | undefined): boolean =>
      status === 'booked' || status === 'blocked' || status === 'hold',
    []
  );

  /** Night-boundary check-in: this day occupied (incl. hold), previous free. */
  const isVisualCheckin = useCallback(
    (date: Date): boolean => {
      if (!isVisuallyOccupied(statusOn(date))) return false;
      return statusOn(addDays(date, -1)) === 'available';
    },
    [isVisuallyOccupied, statusOn]
  );

  /** Night-boundary check-out: this day free, previous occupied (incl. hold). */
  const isVisualCheckout = useCallback(
    (date: Date): boolean => {
      if (statusOn(date) !== 'available') return false;
      return isVisuallyOccupied(statusOn(addDays(date, -1)));
    },
    [isVisuallyOccupied, statusOn]
  );

  const isDateUnavailable = useCallback(
    (date: Date): boolean => {
      const dateData = availability.get(format(date, 'yyyy-MM-dd'));
      return isBlockingStatus(dateData?.status);
    },
    [availability, isBlockingStatus]
  );

  const isDatePast = useCallback(
    (date: Date): boolean => isBefore(date, tomorrow),
    [tomorrow]
  );

  /** Check-in day of existing booking: occupied, but previous day is available */
  const isCheckinDay = useCallback(
    (date: Date): boolean => {
      const dateData = availability.get(format(date, 'yyyy-MM-dd'));
      if (!dateData) return false;
      // Under adminOverride a hold is not "occupied" → it renders as a full
      // selectable (green) day, not a half-day boundary.
      if (!isBlockingStatus(dateData.status)) return false;

      const prevData = availability.get(format(addDays(date, -1), 'yyyy-MM-dd'));
      if (!prevData) return false;
      return prevData.status === 'available';
    },
    [availability, isBlockingStatus]
  );

  /** Checkout day of existing booking: available, but previous day is occupied */
  const isCheckoutDay = useCallback(
    (date: Date): boolean => {
      const dateData = availability.get(format(date, 'yyyy-MM-dd'));
      if (!dateData || dateData.status !== 'available') return false;

      const prevData = availability.get(format(addDays(date, -1), 'yyyy-MM-dd'));
      if (!prevData) return false;
      return isBlockingStatus(prevData.status);
    },
    [availability, isBlockingStatus]
  );

  // ---- Navigation ----

  const goToPrevMonth = () => {
    // El esqueleto aparece solo, en el mismo render: al cambiar a un mes que no está
    // en loadedMonths, showSkeleton pasa a true sin necesidad de tocar ningún flag.
    if (canGoPrev) setCurrentMonth(subMonths(currentMonth, 1));
  };

  const goToNextMonth = () => {
    const next = addMonths(currentMonth, 1);
    setCurrentMonth(next);
    // Pre-fetch +1 month ahead in background
    const lookAhead = addMonths(next, 1);
    const laStart = startOfMonth(lookAhead);
    const laEnd = endOfMonth(lookAhead);
    const tom = addDays(startOfToday(), 1);
    const effectiveStart = isBefore(laStart, tom) ? tom : laStart;
    if (isBefore(effectiveStart, laEnd)) {
      fetchAvailability(effectiveStart, laEnd, true);
    }
  };

  // ---- Range validation ----

  const isRangeValid = useCallback(
    (from: Date, to: Date): boolean => {
      const nights = Math.ceil(
        (to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24)
      );
      if (!adminOverride && nights < effectiveMinNights) return false;

      let current = from;
      while (isBefore(current, to)) {
        if (isDateUnavailable(current)) return false;
        current = addDays(current, 1);
      }
      return true;
    },
    [adminOverride, effectiveMinNights, isDateUnavailable]
  );

  /** Days in the minimum-nights veto window after check-in (lodge only). */
  const isBlockedByMinNights = useCallback(
    (date: Date): boolean => {
      if (adminOverride) return false;
      if (itemType !== 'lodge' || !rangeStart || rangeEnd) return false;
      if (!isAfter(date, rangeStart)) return false;
      const nights = Math.ceil(
        (date.getTime() - rangeStart.getTime()) / (1000 * 60 * 60 * 24)
      );
      return nights < effectiveMinNights;
    },
    [adminOverride, itemType, rangeStart, rangeEnd, effectiveMinNights]
  );

  // ---- Click handlers ----

  const handleRangeClick = (date: Date) => {
    if (disabled || isDatePast(date)) return;

    const unavail = isDateUnavailable(date);
    const checkin = isCheckinDay(date);

    // Fully blocked mid-booking day — reject
    if (unavail && !checkin) return;

    // Vetoed by minimum-nights window — cannot pick as checkout
    if (isBlockedByMinNights(date)) return;

    // Check-in day of existing booking — only allow as checkout target
    if (checkin) {
      if (
        rangeStart &&
        !rangeEnd &&
        isAfter(date, rangeStart) &&
        isRangeValid(rangeStart, date)
      ) {
        setRangeEnd(date);
        const cached = extractCachedRates(rangeStart, date);
        onRangeSelect?.({
          start: rangeStart,
          end: date,
          cachedNightlyRates: cached?.rates,
          cacheComplete: cached?.complete ?? false,
        });
      }
      return;
    }

    // Normal available day
    if (!rangeStart || rangeEnd) {
      setRangeStart(date);
      setRangeEnd(null);
      onRangeSelect?.(null);
    } else {
      if (isBefore(date, rangeStart) || isSameDay(date, rangeStart)) {
        setRangeStart(date);
        setRangeEnd(null);
        onRangeSelect?.(null);
      } else if (isRangeValid(rangeStart, date)) {
        setRangeEnd(date);
        const cached = extractCachedRates(rangeStart, date);
        onRangeSelect?.({
          start: rangeStart,
          end: date,
          cachedNightlyRates: cached?.rates,
          cacheComplete: cached?.complete ?? false,
        });
      } else {
        setRangeStart(date);
        setRangeEnd(null);
        onRangeSelect?.(null);
      }
    }
  };

  const handleDateClick = (date: Date) => {
    if (disabled || isDatePast(date) || isDateUnavailable(date)) return;

    if (selectedDate && isSameDay(selectedDate, date)) {
      setSelectedDate(null);
      onDateSelect?.(null);
    } else {
      setSelectedDate(date);
      onDateSelect?.(date);
    }
  };

  // ---- CSS class builder ----

  const getDayClasses = (date: Date): string => {
    const classes: string[] = [styles.calDay];
    const isPast = isDatePast(date);
    const isUnavail = !isPast && isDateUnavailable(date);
    const checkin = !isPast && isCheckinDay(date);
    const checkout = !isPast && isCheckoutDay(date);

    if (isPast) classes.push(styles.past);
    if (isSameDay(date, startOfToday())) classes.push(styles.calToday);

    // Selection classes first — they override half-day visuals
    let selected = false;
    if (itemType === 'lodge') {
      if (rangeStart && rangeEnd) {
        if (isSameDay(date, rangeStart) && isSameDay(date, rangeEnd)) {
          classes.push(styles.calSel);
          selected = true;
        } else if (isSameDay(date, rangeStart)) {
          if (checkout) {
            // Checkout day as user's check-in → gray/orange diagonal
            classes.push(styles.calCheckoutSel);
          } else {
            classes.push(styles.calSel, styles.calStart);
          }
          selected = true;
        } else if (isSameDay(date, rangeEnd)) {
          if (checkin) {
            // Check-in day as user's checkout → orange/gray diagonal
            classes.push(styles.calCheckinSel);
          } else {
            classes.push(styles.calSel, styles.calEnd);
          }
          selected = true;
        } else if (isAfter(date, rangeStart) && isBefore(date, rangeEnd)) {
          classes.push(styles.calRange);
          selected = true;
        }
      } else if (rangeStart && !rangeEnd && isSameDay(date, rangeStart)) {
        if (checkout) {
          classes.push(styles.calCheckoutSel);
        } else {
          classes.push(styles.calSel, styles.calStart);
        }
        selected = true;
      }
    } else {
      if (selectedDate && isSameDay(date, selectedDate)) {
        classes.push(styles.calSel);
        selected = true;
      }
    }

    // Half-day boundary only when NOT part of user's active selection.
    if (!selected) {
      if (adminOverride) {
        // Admin pay-link calendar: holds are selectable but still render with
        // the night-boundary diagonals (a hold occupies a NIGHT, not a full
        // day). SHAPE comes from isVisual* (hold counts as occupied); COLOUR is
        // green for a hold, gray for a real booked/blocked. Identical footprint
        // to the gray visuals — only the tint differs.
        const here = statusOn(date);
        const prev = statusOn(addDays(date, -1));
        if (!isPast && isVisualCheckin(date)) {
          classes.push(here === 'hold' ? styles.calHoldCheckin : styles.calCheckin);
        } else if (!isPast && isVisualCheckout(date)) {
          classes.push(prev === 'hold' ? styles.calHoldCheckout : styles.calCheckout);
        } else if (!isPast && isVisuallyOccupied(here)) {
          classes.push(here === 'hold' ? styles.calHold : styles.calUnavail);
        }
      } else {
        // Public/customer calendar — unchanged: hold is a blocking status here,
        // so it folds into the gray check-in/check-out/unavailable visuals.
        if (checkin) classes.push(styles.calCheckin);
        else if (checkout) classes.push(styles.calCheckout);
        else if (isUnavail) classes.push(styles.calUnavail);
      }
    }

    // Minimum-nights veto window (while picking checkout, lodge only).
    // Applied after selection logic so it never overrides selected/range cells
    // (isBlockedByMinNights returns false when rangeEnd is set).
    if (!isPast && !isUnavail && isBlockedByMinNights(date)) {
      classes.push(styles.calMinBlocked);
      if (anchorDate && isSameDay(date, anchorDate)) {
        classes.push(styles.calMinBlockedAnchor);
      }
    }

    return classes.join(' ');
  };

  // ---- Availability fetching ----

  /**
   * Fetch availability data.
   * @param silent - When true, fetches in background without blocking UI (stale-while-revalidate)
   */
  const fetchAvailability = useCallback(
    async (startDate: Date, endDate: Date, silent = false) => {
      const monthKey = `${format(startDate, 'yyyy-MM')}-${format(endDate, 'yyyy-MM')}`;
      if (fetchedMonthsRef.current.has(monthKey)) return;

      if (abortControllerRef.current) abortControllerRef.current.abort();
      abortControllerRef.current = new AbortController();

      setError(null);

      try {
        const startStr = format(startDate, 'yyyy-MM-dd');
        const endStr = format(endDate, 'yyyy-MM-dd');
        const params = new URLSearchParams({
          startDate: startStr,
          endDate: endStr,
          guests: guests.toString(),
        });
        // Pulsera de sesión del visitante: que el calendario NO le tape al
        // cliente sus propias fechas en hold. En el pay-link admin
        // (adminOverride) NO se envía: el admin debe VER todos los holds
        // (propios o ajenos) como overrideables (verdes), sin que el backend
        // libere ninguno por sesión. Ver lib/session.ts.
        if (!adminOverride) {
          params.set('sessionId', getSessionId());
        }

        const apiBase = process.env.NEXT_PUBLIC_API_URL;
        const apiUrl = apiBase ? `${apiBase}/api` : '/api';
        const response = await fetch(
          `${apiUrl}/availability/${itemType}/${itemId}?${params}`
        );

        if (!response.ok) throw new Error('Failed to fetch availability');

        const responseData = await response.json();
        const dates =
          responseData?.data?.availability?.dates ||
          responseData?.dates ||
          [];

        setAvailability((prev) => {
          const newMap = new Map(prev);
          if (Array.isArray(dates)) {
            dates.forEach((dateData: DateAvailabilityData) => {
              newMap.set(dateData.date, dateData);
            });
          }
          return newMap;
        });

        fetchedMonthsRef.current.add(monthKey);

        // Marcar como cargados los meses que cubre este fetch (incluye respuestas
        // vacías: el mes se intentó → el esqueleto no queda pegado en meses lejanos).
        setLoadedMonths((prev) => {
          const next = new Set(prev);
          let cur = startOfMonth(startDate);
          const lastMonth = startOfMonth(endDate);
          while (!isAfter(cur, lastMonth)) {
            next.add(format(cur, 'yyyy-MM'));
            cur = addMonths(cur, 1);
          }
          return next;
        });

        // Subtle refresh indicator for background fetches
        if (silent) {
          if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
          setRefreshed(true);
          refreshTimeoutRef.current = setTimeout(() => setRefreshed(false), 1500);
        }
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return;
        if (!silent) {
          setError(CALENDAR_I18N[locale]?.error || CALENDAR_I18N.en.error);
        }
        console.error('Availability fetch error:', err);
      }
    },
    [itemType, itemId, guests, adminOverride]
  );

  // Cleanup refresh animation timeout
  useEffect(() => {
    return () => {
      if (refreshTimeoutRef.current) clearTimeout(refreshTimeoutRef.current);
    };
  }, []);

  // Keep ref in sync with state (avoids dependency loop in month change effect)
  useEffect(() => {
    availabilityRef.current = availability;
  }, [availability]);

  // Reset al cambiar de propiedad (itemId/itemType): limpia TODA la cache
  // por-propiedad para que el calendario vuelva a pedir disponibilidad de
  // INMEDIATO, no en el refresco de 30s. Sin esto, el guard de
  // fetchedMonthsRef (cuyo monthKey NO incluye el itemId) bloquea el refetch
  // y la grilla sigue mostrando la disponibilidad de la propiedad anterior.
  // Se declara ANTES del efecto de fetch-por-mes para que ese lea las caches
  // ya vacías y dispare un fetch bloqueante (con esqueleto) de la nueva
  // propiedad. El guard de prevItemKeyRef evita resetear en el primer render
  // (preserva initialRange/initialDate del flujo de cliente).
  const prevItemKeyRef = useRef<string>(`${itemType}:${itemId}`);
  useEffect(() => {
    const key = `${itemType}:${itemId}`;
    if (prevItemKeyRef.current === key) return;
    prevItemKeyRef.current = key;

    abortControllerRef.current?.abort();
    fetchedMonthsRef.current.clear();
    availabilityRef.current = new Map();
    setAvailability(new Map());
    setLoadedMonths(new Set()); // → esqueleto hasta que cargue la nueva propiedad
    setError(null);
    // La selección previa pertenece a la propiedad anterior: ya no es válida.
    setRangeStart(null);
    setRangeEnd(null);
    setSelectedDate(null);
  }, [itemType, itemId]);

  // Fetch on month change — pre-fetch +1 month, silent if cached
  useEffect(() => {
    const tom = addDays(startOfToday(), 1);
    const mStart = startOfMonth(currentMonth);
    const mEnd = endOfMonth(addMonths(currentMonth, 1));
    const effectiveStart = isBefore(mStart, tom) ? tom : mStart;
    if (!isBefore(effectiveStart, mEnd)) return;

    // If current month already has data → fetch silently (no overlay)
    const hasCachedData = hasMonthData(
      availabilityRef.current,
      currentMonth.getFullYear(),
      currentMonth.getMonth()
    );
    fetchAvailability(effectiveStart, mEnd, hasCachedData);

    return () => {
      abortControllerRef.current?.abort();
    };
  }, [currentMonth, fetchAvailability]);

  // Auto-refresh every 30s — silent (no blocking overlay), +1 month
  useEffect(() => {
    const refreshInterval = setInterval(() => {
      fetchedMonthsRef.current.clear();
      const tom = addDays(startOfToday(), 1);
      const mStart = startOfMonth(currentMonth);
      const mEnd = endOfMonth(addMonths(currentMonth, 1));
      const effectiveStart = isBefore(mStart, tom) ? tom : mStart;
      if (isBefore(effectiveStart, mEnd)) fetchAvailability(effectiveStart, mEnd, true);
    }, 30000);
    return () => clearInterval(refreshInterval);
  }, [currentMonth, fetchAvailability]);

  // Refresh on tab visibility — silent (no blocking overlay), +1 month
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchedMonthsRef.current.clear();
        const tom = addDays(startOfToday(), 1);
        const mStart = startOfMonth(currentMonth);
        const mEnd = endOfMonth(addMonths(currentMonth, 1));
        const effectiveStart = isBefore(mStart, tom) ? tom : mStart;
        if (isBefore(effectiveStart, mEnd))
          fetchAvailability(effectiveStart, mEnd, true);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () =>
      document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [currentMonth, fetchAvailability]);

  // Backup fetch: when the user picks a check-in date, fetch min nights for that
  // specific date from the API. Primary source is the Map loaded by the month
  // fetch (perDateMinNights); this is a silent fallback for edge cases.
  useEffect(() => {
    if (!rangeStart || itemType !== 'lodge') {
      setCheckInMinNights(null);
      return;
    }
    let cancelled = false;
    const startStr = format(rangeStart, 'yyyy-MM-dd');
    const endStr = format(addDays(rangeStart, 1), 'yyyy-MM-dd');
    const params = new URLSearchParams({
      startDate: startStr,
      endDate: endStr,
      guests: guests.toString(),
    });
    if (!adminOverride) params.set('sessionId', getSessionId());
    const apiBase = process.env.NEXT_PUBLIC_API_URL;
    const apiUrl = apiBase ? `${apiBase}/api` : '/api';
    fetch(`${apiUrl}/availability/${itemType}/${itemId}?${params}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((data) => {
        if (!cancelled) {
          const mn = data?.data?.requirements?.minimumNights;
          if (typeof mn === 'number') setCheckInMinNights(mn);
        }
      })
      .catch(() => {/* respaldo silencioso — el Map del mes es la fuente primaria */});
    return () => {
      cancelled = true;
    };
  }, [rangeStart, itemType, itemId, guests, adminOverride]);

  // Re-validate the range when effectiveMinNights changes (e.g. backup fetch resolved).
  // If the already-confirmed range is shorter than the effective minimum, clear checkout.
  useEffect(() => {
    if (adminOverride) return;
    if (!rangeStart || !rangeEnd) return;
    const nights = Math.ceil(
      (rangeEnd.getTime() - rangeStart.getTime()) / (1000 * 60 * 60 * 24)
    );
    if (nights < effectiveMinNights) {
      setRangeEnd(null);
      onRangeSelect?.(null);
    }
  }, [adminOverride, checkInMinNights, rangeStart, rangeEnd, effectiveMinNights, onRangeSelect]);

  // ---- Helpers for render ----

  const getDateData = (date: Date): DateAvailabilityData | undefined => {
    return availability.get(format(date, 'yyyy-MM-dd'));
  };

  /** Extract cached nightly rates from availability Map for a date range */
  const extractCachedRates = (
    from: Date,
    to: Date
  ): { rates: Record<string, number>; complete: boolean } | null => {
    const rates: Record<string, number> = {};
    let complete = true;
    let current = new Date(from);
    while (current < to) {
      const key = format(current, 'yyyy-MM-dd');
      const data = availability.get(key);
      if (!data || data.price <= 0) {
        complete = false;
      } else {
        rates[key] = data.price;
      }
      current = addDays(current, 1);
    }
    return Object.keys(rates).length > 0 ? { rates, complete } : null;
  };

  // ---- Render ----

  return (
    <div
      className={`${styles.container} ${variant === 'flat' ? styles.flat : ''} ${className || ''}`}
    >
      {/* Header with title */}
      {!hideHeader && (
        <div className={styles.header}>
          <h3 className={styles.title}>
            {itemType === 'lodge' ? i18n.selectDates : i18n.selectDate}
          </h3>
          {itemType === 'lodge' && effectiveMinNights > 1 && (
            <p className={styles.subtitle}>
              {rangeStart && !rangeEnd ? (
                <>
                  <span className={styles.minNightsSwatch} aria-hidden="true" />
                  {i18n.minimumNights(effectiveMinNights)}
                </>
              ) : (
                i18n.minimumNights(minimumNights)
              )}
            </p>
          )}
        </div>
      )}

      {/* Subtle refresh indicator (background refresh completed) */}
      {refreshed && <div className={styles.refreshIndicator} />}

      {/* Error message */}
      {error && <div className={styles.error}>{error}</div>}

      {/* Cabecera de navegación: SIEMPRE visible y clickeable, también mientras
          carga, para poder seguir cambiando de mes con las flechas. El esqueleto
          vive solo en la grilla de fechas (abajo). */}
      <div className={styles.calHeader}>
        <button
          className={styles.calNavBtn}
          onClick={goToPrevMonth}
          disabled={!canGoPrev}
          aria-label={i18n.prevMonth}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        <span className={styles.calMonthName}>{monthName}</span>
        <button
          className={styles.calNavBtn}
          onClick={goToNextMonth}
          aria-label={i18n.nextMonth}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M9 18l6-6-6-6" />
          </svg>
        </button>
      </div>

      {/* Grilla de fechas: esqueleto mientras carga el mes mostrado; grilla real
          cuando su disponibilidad está lista. Misma estructura de 7 columnas y
          mismo alto de celda → sin salto de layout al intercambiar. */}
      {showSkeleton ? (
        <div className={styles.skeletonGrid}>
          {Array.from({ length: 7 }).map((_, i) => (
            <div key={`sh-${i}`} className={styles.skeletonDayHeader}>
              <span style={{ '--i': i } as React.CSSProperties} />
            </div>
          ))}
          {Array.from({ length: 35 }).map((_, i) => {
            const row = Math.floor(i / 7);
            const col = i % 7;
            return (
              <div
                key={`sd-${i}`}
                className={styles.skeletonDay}
                style={{ '--i': 7 + row + col } as React.CSSProperties}
              />
            );
          })}
        </div>
      ) : (
        <div className={styles.calGrid}>
          {/* Day headers */}
          {dayHeaders.map((dh) => (
            <div key={dh} className={styles.calDh}>
              {dh}
            </div>
          ))}

          {/* Day cells */}
          {calendarDays.map((date, idx) => {
            if (!date) {
              return (
                <div
                  key={`empty-${idx}`}
                  className={`${styles.calDay} ${styles.calEmpty}`}
                />
              );
            }

            const isPast = isDatePast(date);
            const isUnavail = !isPast && isDateUnavailable(date);
            const dateData = getDateData(date);
            const price = dateData?.price;
            const showPrice = showPrices && price && !isPast && !isUnavail;

            return (
              <div
                key={date.getDate()}
                className={getDayClasses(date)}
                title={
                  !isPast && isOverridableHold(date)
                    ? 'On hold — you can still select it (admin override)'
                    : undefined
                }
                onClick={() =>
                  itemType === 'lodge'
                    ? handleRangeClick(date)
                    : handleDateClick(date)
                }
              >
                <span className={styles.calDn}>{date.getDate()}</span>
                {showPrice && (
                  <span className={styles.calDp}>
                    {currency === 'CLP'
                      ? `$${Math.round(price / 1000)}k`
                      : `$${price}`}
                  </span>
                )}
                {anchorDate && isSameDay(date, anchorDate) && (
                  <span
                    className={[
                      styles.minNightsLabel,
                      blockedCount % 2 === 0 ? styles.minNightsLabelEven : '',
                      date.getDay() === 6 ? styles.minNightsLabelEndRow : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    {i18n.minNightsShort(effectiveMinNights)}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Legend — siempre real (no es parte de "las fechas") */}
      {!hideLegend && (
        <div className={styles.calLegend}>
          <span className={styles.calLegendItem}>
            <span className={`${styles.clDot} ${styles.dotAvailable}`} />
            <span>{i18n.available}</span>
          </span>
          <span className={styles.calLegendItem}>
            <span className={`${styles.clDot} ${styles.dotSelected}`} />
            <span>{i18n.selected}</span>
          </span>
          <span className={styles.calLegendItem}>
            <span className={`${styles.clDot} ${styles.dotUnavailable}`} />
            <span>{i18n.unavailable}</span>
          </span>
          {adminOverride && (
            <span className={styles.calLegendItem}>
              <span className={`${styles.clDot} ${styles.dotHold}`} />
              <span>On hold (overridable)</span>
            </span>
          )}
        </div>
      )}

      {/* Activity mode: selection summary */}
      {itemType === 'activity' && selectedDate && (
        <div className={styles.selectionSummary}>
          <div className={styles.summaryRow}>
            <span>{i18n.date}</span>
            <strong>
              {format(selectedDate, 'PPP', { locale: dateLocale })}
            </strong>
          </div>
          {showPrices && getDateData(selectedDate)?.price && (
            <div className={styles.summaryPrice}>
              <span>{i18n.price}</span>
              <strong className={styles.priceTotal}>
                {formatPriceDisplay(
                  getDateData(selectedDate)!.price,
                  getDateData(selectedDate)!.currency || currency
                )}
              </strong>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
